/**
 * Space Design
 *
 * Shared visual contracts for Spaces work surfaces.
 *
 * Primary responsibilities:
 * - Define Spaces density tiers and item tones
 * - Provide repeated card, empty state, and action target classes
 *
 * Dependencies:
 * - lib/utils
 */

import type { ReactNode } from "react"

import { cn } from "@/lib/utils"

export type SpaceDensityTier = "compact" | "medium" | "canvas"

export type SpaceItemToneKey =
  | "task"
  | "folder"
  | "sprint"
  | "note"
  | "design"
  | "sheet"
  | "conversation"
  | "chat"
  | "whiteboard"
  | "file"
  | "event"

type SpaceItemTone = {
  label: string
  icon: string
  chip: string
  rail: string
}

export const spaceDensityClassMap: Record<SpaceDensityTier, string> = {
  compact: "gap-2 p-2",
  medium: "gap-3 p-3 sm:gap-4 sm:p-4",
  canvas: "gap-4 p-3 sm:gap-6 sm:p-6"
}

export const spaceItemToneMap: Record<SpaceItemToneKey, SpaceItemTone> = {
  task: {
    label: "Task",
    icon: "text-emerald-500 dark:text-emerald-300",
    chip:
      "border-emerald-400/25 bg-emerald-400/10 text-emerald-500 dark:text-emerald-300",
    rail: "bg-emerald-500"
  },
  folder: {
    label: "Folder",
    icon: "text-sky-500 dark:text-sky-300",
    chip:
      "border-sky-400/25 bg-sky-400/10 text-sky-500 dark:text-sky-300",
    rail: "bg-sky-500"
  },
  sprint: {
    label: "Sprint",
    icon: "text-indigo-500 dark:text-indigo-300",
    chip:
      "border-indigo-400/25 bg-indigo-400/10 text-indigo-500 dark:text-indigo-300",
    rail: "bg-indigo-500"
  },
  note: {
    label: "Note",
    icon: "text-amber-500 dark:text-amber-300",
    chip:
      "border-amber-400/25 bg-amber-400/10 text-amber-500 dark:text-amber-300",
    rail: "bg-amber-500"
  },
  design: {
    label: "Design",
    icon: "text-fuchsia-500 dark:text-fuchsia-300",
    chip:
      "border-fuchsia-400/25 bg-fuchsia-400/10 text-fuchsia-500 dark:text-fuchsia-300",
    rail: "bg-fuchsia-500"
  },
  sheet: {
    label: "Sheet",
    icon: "text-cyan-500 dark:text-cyan-300",
    chip:
      "border-cyan-400/25 bg-cyan-400/10 text-cyan-500 dark:text-cyan-300",
    rail: "bg-cyan-500"
  },
  conversation: {
    label: "Conversation",
    icon: "text-violet-500 dark:text-violet-300",
    chip:
      "border-violet-400/25 bg-violet-400/10 text-violet-500 dark:text-violet-300",
    rail: "bg-violet-500"
  },
  chat: {
    label: "Chat",
    icon: "text-violet-500 dark:text-violet-300",
    chip:
      "border-violet-400/25 bg-violet-400/10 text-violet-500 dark:text-violet-300",
    rail: "bg-violet-500"
  },
  whiteboard: {
    label: "Whiteboard",
    icon: "text-pink-500 dark:text-pink-300",
    chip:
      "border-pink-400/25 bg-pink-400/10 text-pink-500 dark:text-pink-300",
    rail: "bg-pink-500"
  },
  file: {
    label: "File",
    icon: "text-slate-500 dark:text-slate-300",
    chip:
      "border-slate-400/25 bg-slate-400/10 text-slate-500 dark:text-slate-300",
    rail: "bg-slate-500"
  },
  event: {
    label: "Event",
    icon: "text-blue-500 dark:text-blue-300",
    chip:
      "border-blue-400/25 bg-blue-400/10 text-blue-500 dark:text-blue-300",
    rail: "bg-blue-500"
  }
}

export const spaceRepeatedCardClass =
  "border-border/60 bg-card text-card-foreground min-w-0 overflow-hidden rounded-lg border shadow-sm"

export const spaceRepeatedCardInteractiveClass =
  "cursor-pointer touch-manipulation hover:border-border hover:bg-muted/30 hover:shadow-sm active:motion-safe:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 motion-safe:transition-[background-color,border-color,box-shadow,transform] motion-safe:duration-200 motion-safe:ease-out motion-reduce:transition-none"

export const spaceOverviewCardClass =
  "border-border/70 bg-background/95 overflow-hidden rounded-lg border shadow-[0_18px_54px_-44px_rgba(15,23,42,0.65)] dark:bg-white/[0.035]"

export const spaceActionTargetClass =
  "min-h-10 min-w-10 touch-manipulation sm:min-h-9 sm:min-w-9"

export const spaceCompactActionTargetClass =
  "min-h-10 min-w-10 touch-manipulation sm:min-h-8 sm:min-w-8"

export const spaceInlineActionClass =
  "min-h-10 touch-manipulation px-3 text-xs sm:min-h-8 sm:px-2"

export const getSpaceItemTone = (
  itemType: string | null | undefined
): SpaceItemTone => {
  const normalizedItemType = itemType as SpaceItemToneKey | null | undefined

  return normalizedItemType && normalizedItemType in spaceItemToneMap
    ? spaceItemToneMap[normalizedItemType]
    : spaceItemToneMap.file
}

export const getSpaceItemTypeLabel = (
  itemType: string | null | undefined
) => getSpaceItemTone(itemType).label

type SpaceItemIconChipProps = {
  children: ReactNode
  className?: string
  itemType: string | null | undefined
}

export const SpaceItemIconChip = ({
  children,
  className,
  itemType
}: SpaceItemIconChipProps) => (
  <span
    className={cn(
      "flex size-9 shrink-0 items-center justify-center rounded-lg border",
      getSpaceItemTone(itemType).chip,
      className
    )}
  >
    {children}
  </span>
)

type SpaceEmptyStateProps = {
  action?: ReactNode
  className?: string
  description?: string
  density?: SpaceDensityTier
  icon?: ReactNode
  title: string
  tone?: SpaceItemToneKey
}

const emptyStateClassMap: Record<SpaceDensityTier, string> = {
  compact: "px-3 py-4",
  medium: "px-6 py-8",
  canvas: "px-6 py-10 sm:px-8 sm:py-12"
}

export const SpaceEmptyState = ({
  action,
  className,
  density = "medium",
  description,
  icon,
  title,
  tone = "file"
}: SpaceEmptyStateProps) => (
  <div
    className={cn(
      "border-border/60 bg-background/95 flex flex-col items-center rounded-lg border border-dashed text-center dark:bg-white/[0.03]",
      emptyStateClassMap[density],
      className
    )}
  >
    {icon ? (
      <span
        className={cn(
          "mb-3 flex size-11 items-center justify-center rounded-full border",
          getSpaceItemTone(tone).chip
        )}
      >
        {icon}
      </span>
    ) : null}
    <p className="text-foreground text-sm font-semibold">{title}</p>
    {description ? (
      <p className="text-muted-foreground mt-1 max-w-sm text-xs leading-5 sm:text-sm">
        {description}
      </p>
    ) : null}
    {action ? <div className="mt-4">{action}</div> : null}
  </div>
)
